import { useState, useEffect, useMemo, useCallback } from 'react';
import { objectCompare } from '~/utils/compare';
import { useLocation } from '@reach/router';
import { useLocalStorage } from '~/hooks/components/use-storage';

import getQueryParams from '~/utils/get-query-params';
import useVariantAvailability from './use-variant-availability';
import useGetFirstAvailableVariantOrNull from './use-get-first-available-variant-or-null';
import useVariantAnalytics from '../utils/customAnalytics/use-variant-analytics';

const useProductVariants = (
  product: Queries.ProductDetailsFragmentFragment,
  locationDetail: string,
  collection: string,
  parentProductSlug: string
) => {
  const { trackVariantClicked } = useVariantAnalytics(parentProductSlug);
  const { variants } = product;
  const { value: womensSize } = useLocalStorage('womensSize');
  const { value: mensSize } = useLocalStorage('mensSize');

  const { pathname, search } = useLocation();

  const queryParams = getQueryParams(search);

  const getQueryParamValue = (param: string): string => {
    const value = queryParams[param];
    return typeof value === 'string' ? decodeURIComponent(value).replace(/\/+$/, '') : '';
  };

  const colorQuery = getQueryParamValue('color');
  const amountQuery = getQueryParamValue('amount');

  const desiredProductRange = useMemo(() => {
    const range = product?.variants?.reduce((acc: number[], variant, i) => {
      if (variant?.optionValues?.some((ov) => ov?.slug?.includes(colorQuery))) {
        return [...acc, i];
      }
      return acc;
    }, []);
    if (!range?.length) return [0, 0]; // Return default range if not available
    return range;
  }, [product, colorQuery]);
  // Go through each variant until we hit one in stock.
  const { availableVariant, isLoading } = useGetFirstAvailableVariantOrNull({
    variants,
    desiredColorRange: [desiredProductRange[0], desiredProductRange[desiredProductRange.length - 1]],
    shouldFetch: !colorQuery,
  });

  const [currentVariant, setCurrentVariant] = useState<Queries.VariantDetailsFragmentFragment | null>(
    variants?.[0] ?? null
  );
  const [savedVariant, setSavedVariant] = useState<Queries.VariantDetailsFragmentFragment | null>(null);
  const [loadSavedVariant, setLoadSavedVariant] = useState(true);

  const getIsWomens = useMemo(() => pathname.split('/').some((p) => p.includes('womens')), [pathname]);

  const getSavedSize = useCallback(
    (isWomens: boolean, isVest: boolean) => {
      if (isWomens) {
        return isVest ? womensSize?.vest?.trim() : womensSize?.apparel?.trim();
      }
      return isVest ? mensSize?.vest?.trim() : mensSize?.apparel?.trim();
    },
    [womensSize, mensSize]
  );

  const matchingVariant = useMemo(() => {
    const savedColor = colorQuery;
    const savedAmount = amountQuery;
    return variants?.find((v) => {
      const color = v?.optionValues?.find((ov) => ov?.slug?.includes('color'));
      const size = v?.optionValues?.find((ov) => ov?.slug?.includes('size'));
      const amount = v?.optionValues?.find((ov) => ov?.slug?.includes('amount'));

      const isVest = v?.name?.includes('Vest');
      const savedSize = getSavedSize(getIsWomens, !!isVest);
      const colorMatch = savedColor && savedColor.toLowerCase() === color?.presentation?.toLowerCase();
      const sizeMatch = savedSize && savedSize === size?.presentation;
      const amountMatch = amount?.presentation === savedAmount;

      return (
        (colorMatch && sizeMatch) ||
        (!savedColor && sizeMatch) ||
        (colorMatch && !savedSize) ||
        colorMatch ||
        (savedAmount && amountMatch)
      );
    });
  }, [variants, colorQuery, amountQuery, getIsWomens, getSavedSize]);

  useEffect(() => {
    setSavedVariant(matchingVariant ?? variants?.[0] ?? null);
    setLoadSavedVariant(false);
  }, [matchingVariant, variants]);

  const {
    data: isAvailable,
    isLoading: isFetchingAvailability,
    error,
  } = useVariantAvailability({ sku: savedVariant?.sku ?? '', shouldFetch: !!savedVariant?.sku });

  useEffect(() => {
    if (!isLoading && !loadSavedVariant) {
      if (savedVariant || availableVariant) {
        // Only update if savedVariant is available or if availableVariant is unavailable but saved variant exists
        // or if saved variant doesn't even exist.

        if (savedVariant && !isFetchingAvailability && !error) {
          setCurrentVariant(savedVariant);
        } else if (!isFetchingAvailability) {
          setCurrentVariant(availableVariant || savedVariant);
        } else {
          setCurrentVariant(availableVariant || currentVariant);
        }
      }
    }
  }, [savedVariant, isLoading, loadSavedVariant, isAvailable, isFetchingAvailability, availableVariant]);

  /**
   * Find and set a variant given a change in option values.
   */
  const selectVariant = (option: Queries.OptionValuesFragmentFragment) => {
    const newOptions = getNewOptionValues(option);
    if (!newOptions) return;
    const results = getVariantForOptionValues(newOptions);
    if (!results) {
      // TODO: alert users that this combination is not available
      // https://app.clubhouse.io/arfa/story/3318/variants-add-support-to-display-unavailable-option-values
      console.warn(`Cannot find variant with options: ${JSON.stringify(newOptions.map((o) => o?.presentation))}`);
    } else {
      trackVariantClicked({ variant: results, locationDetail, collection });
      setCurrentVariant(results);
    }
  };

  /**
   * Return a set of option values based on our initial state and a newly selected option.
   */
  const getNewOptionValues = (option: Queries.OptionValuesFragmentFragment) =>
    currentVariant?.optionValues?.map((o) =>
      o?.option_type?.[0]?.slug === option?.option_type?.[0]?.slug ? option : o
    );

  /**
   * Return a variant for a given list of option values.
   */
  const getVariantForOptionValues = (options: (Queries.OptionValuesFragmentFragment | null)[] | null) =>
    variants?.find((v) => {
      const sortedVariant: (Queries.OptionValuesFragmentFragment | null | undefined)[] = [];
      const sortedOptions: (Queries.OptionValuesFragmentFragment | null | undefined)[] = [];

      // Check if the variant has an 'amount' option
      const amountOption = v?.optionValues?.find((ov) => ov?.slug?.includes('amount'));
      if (amountOption) {
        // If 'amount' option exists, it's the only option we need to compare
        sortedVariant[0] = amountOption;
        sortedOptions[0] = options?.find((ov) => ov?.slug?.includes('amount'));
      } else {
        // If no 'amount' option, use the existing color and size logic
        sortedVariant[0] = v?.optionValues?.find((ov) => ov?.slug?.includes('color'));
        sortedVariant[1] = v?.optionValues?.find((ov) => ov?.slug?.includes('size'));
        sortedOptions[0] = options?.find((ov) => ov?.slug?.includes('color'));
        sortedOptions[1] = options?.find((ov) => ov?.slug?.includes('size'));
      }

      return objectCompare(sortedVariant, sortedOptions);
    });

  return { currentVariant, selectVariant };
};

export default useProductVariants;
